import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import WglnLogDetailsModal from 'react/containers/modals/WglnLogDetailsModal/WglnLogDetailsModal';
import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import {
  SelectField,
  TextField,
  DateField,
  LookupField
} from 'react/components/fields';

import { getResourceTo } from 'react/utils/utils';
import { actionCreators as wglnLogActions } from 'react/api/actions/wglnIntegrationLogs';
import { actionCreators as modalsActions } from 'react/api/actions/modals';
import { useGRID } from 'react/hooks/useGrid';

function WGLNIntegrationLogCrud(props) {
  const {
    getDateRangeFilters,
    getActualPage,
    setSortByClient,
    setCustomCustomizeColumns,
    setCustomFilterBuilderFields,
    setCustomOnClickRow,
    setCustomColumnsFormatter,
    setCustomSaveList,
    addDataToGrid
  } = useGRID('WGLNIntegrationLog');

  const dispatch = useDispatch();
  const wglnIntegrationLogs = useSelector((state) => state.wglnIntegrationLogs.get('data', []));

  const nameSpace = useMemo(() => 'WGLNIntegrationLog', []);

  const hasAValidFilterValue = (value) =>
    (typeof(value) === 'string' && String(value)?.length) ||
    (typeof(value) === 'number' && value >= 0);

  const getActiveFilters = () => {
    const fields = App.Forms[nameSpace]?.filterBuilder?.fields || {};
    const activeFilters = {};

    Object.keys(fields).forEach((key) => {
      const filter = fields[key];

      switch (filter.name) {
        case 'requestDate_Range': {
          const customFormatter = (date) => new Date(date).toISOString();

          const { startDate, endDate } = getDateRangeFilters(filter, customFormatter);

          Object.assign(activeFilters, {
            startDate,
            endDate
          });

          break;
        }
        case 'organization': {
          const { model: { key: organizationKey } } = filter.getFullValue();

          if (organizationKey) {
            activeFilters.organizationKey = organizationKey;
          }

          break;
        }
        default: {
          const filterValue = filter.getValue();

          if (hasAValidFilterValue(filterValue)) {
            activeFilters[key] = filterValue;
          }

          break;
        }
      }
    });

    return activeFilters;
  };

  const getSafeMessageValue = ({
    message,
    status,
    i18nMessage,
    errorMessages
  }) => {
    if (status === 200) return '-';

    if (i18nMessage) return i18nMessage;

    if (errorMessages[0].resource.value) return errorMessages[0].resource.value;

    return message;
  }

  const getCustomColumnsFormatters = () => ({
    status: {
      width: 68,
      formatter: (value) => `<div class='httpStatus-${value === 200 ? 'success' : 'error'}'></div>`
    },
    'organization.description': {
      formatter: (_, dataContext) => dataContext.organizationKey
    },
    'message': {
      formatter: (value, dataContext) => {
        if (!value || value === 'null') return '';

        const { status } = dataContext;
        const { i18nMessage, errorMessages } = JSON.parse(value);

        return getSafeMessageValue({
          message: value,
          errorMessages,
          i18nMessage,
          status
        });
      }
    },
    routeKey: {
      width: 280,
    }
  });

  const overrideSaveList = () => {
    const fieldsToOverride = {
      'organization.description': 'organization'
    };

    const customFormatters = {
      organization: (_, data) => data.organizationKey,
      message: (_, data) => {
        const { status, message } = data;

        if (!message || message === 'null') return '';

        const { i18nMessage, errorMessages } = JSON.parse(message);

        return getSafeMessageValue({
          message,
          errorMessages,
          i18nMessage,
          status
        });
      }
    };

    setCustomSaveList('wGLNLogs', fieldsToOverride, customFormatters);
  };

  const setCustomExecuteFilter = () => {
    App.Forms[nameSpace].filterBuilder.customExecuteFilter = () => {
      Monet.Grid.Utils.hideGridNoResult(nameSpace);

      const page = getActualPage();

      if (page === 0)
        App.Forms[nameSpace].pagination.blocked = false;

      const activeFilters = getActiveFilters();

      const filtersPayload = {
        page: Math.ceil(page),
        pageSize: 51
      };

      Object.assign(filtersPayload, activeFilters);

      dispatch(wglnLogActions.fetchLogs({ filtersPayload }));
    };
  };

  const customOnclick = (attributes) => {
     const {
      routeDate,
      requestDate,
      status,
      message,
      actionPerformed,
      organizationKey,
      routeKey
    } = attributes;

    dispatch(modalsActions.openModal({
      modalKey: 'wglnLogDetails',
      title: 'IntegrationLogModal.Title',
      data: {
        routeDate: Monet.Format.Date.formatToLocale(routeDate),
        requestDate: Monet.Format.DateTime.formatToLocale(requestDate),
        status,
        message,
        actionPerformed,
        organizationKey,
        routeKey
      },
    }));
  };

  const beforeList = () => {
    overrideSaveList();
    setCustomCustomizeColumns();
    setSortByClient();
    setCustomFilterBuilderFields({
      fieldsToRemove: ['status', 'message', 'requestDate', 'routeDate_Range']
    });
    setCustomOnClickRow(customOnclick)
    setCustomColumnsFormatter(getCustomColumnsFormatters());
    setCustomExecuteFilter();
  };

  useEffect(() => {
   if (wglnIntegrationLogs?.size >= 0) {
     const data = wglnIntegrationLogs.toJS();

     addDataToGrid(data);
   }
  }, [wglnIntegrationLogs]);

  return (
    <>
      <BaseCrud
        isGrid
        entity="WGLNIntegrationLog"
        customOptions={{
          columnCheckBox: {
            show: false,
          },
          rowHeight: 32,
          sanitizer: null
        }}
        gridCallbacks={{
          beforeList
        }}
        updateTitle={props.updateTitle}
      >
        <FormSection title={getResourceTo('EventLog')} className="generalSection">
          <FormSectionColumn>
            <TextField
              label={getResourceTo('wglnIntegration.field.status')}
              name="status"
            />
            <TextField
              label={getResourceTo('wglnIntegration.field.routeKey')}
              name="routeKey"
            />
            <LookupField
              label={getResourceTo('wglnIntegration.field.organization')}
              name="organization"
              lookupEntity="Organization"
              lookupEntityColumn="description"
            />
            <DateField
              label={getResourceTo('wglnIntegration.field.routeDate')}
              name="routeDate"
              type="DATE"
            />
            <SelectField
              label={getResourceTo('wglnIntegration.field.actionPerformed')}
              name="actionPerformed"
              options={[
                {
                  label: 'ROUTE_CREATION',
                  value: 'ROUTE_CREATION'
                },
                {
                  label: 'ADD_STOP',
                  value: 'ADD_STOP'
                },
                {
                  label: 'UPDATE_STOP',
                  value: 'UPDATE_STOP'
                }
              ]}
            />
            <DateField
              label={getResourceTo('wglnIntegration.field.actionTime')}
              name="requestDate"
              type="DATE_TIME"
            />
            <TextField
              label={getResourceTo('wglnIntegration.field.message')}
              name="message"
            />
          </FormSectionColumn>
        </FormSection>
      </BaseCrud>
      <WglnLogDetailsModal />
    </>
  );
}

WGLNIntegrationLogCrud.propTypes = {
  updateTitle: PropTypes.func.isRequired
};

export default WGLNIntegrationLogCrud;
